// AdminSpacesSection — admin UI for Spaces management.
// Mirrors AdminDepartments class names and patterns from Admin.tsx.

import { useState, useEffect } from "preact/hooks";
import { Plus, Trash2, Users, ChevronDown, ChevronRight, Pencil, X, Check, Building2 } from "lucide-preact";
import {
  useAdminSpaces,
  createAdminSpace,
  assignAdminSpaceMember,
  removeAdminSpaceMember,
  renameAdminSpace,
  grantAdminSpaceDepartment,
  revokeAdminSpaceDepartment,
} from "../../store/adminSpaces";
import { useDepartments } from "../../store/departments";
import { api } from "../../lib/api";
import { toastError, toastSuccess } from "../../lib/toast";
import { COLOR_PRESETS } from "../../lib/format";

import { Button } from "../ui/Button";

interface AdminUser {
  id: number;
  email: string;
  name: string;
  is_admin: boolean;
  is_active: boolean;
  is_external: boolean;
}

function useAdminUsers(): AdminUser[] {
  const [users, setUsers] = useState<AdminUser[]>([]);
  useEffect(() => {
    api<AdminUser[]>("/api/admin/users")
      .then((all) =>
        setUsers(all.filter((u) => u.is_active && !u.is_external))
      )
      .catch(() => {
        // Das Auswahlfeld „Mitglied hinzufuegen" bliebe sonst leer und saehe
        // aus wie „es gibt keine Benutzer". api() toastet nur Mutationen.
        toastError("Benutzerliste konnte nicht geladen werden.");
      });
  }, []);
  return users;
}

interface EditDraft {
  name: string;
  color: string | null;
}

export function AdminSpacesSection(): preact.JSX.Element {
  const spaces = useAdminSpaces();
  const users = useAdminUsers();
  const depts = useDepartments();

  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  // Per-space expanded state (members panel)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Per-space departments panel expanded state
  const [deptExpandedIds, setDeptExpandedIds] = useState<Set<number>>(new Set());

  // Per-space edit state: spaceId -> EditDraft | null (null = not editing)
  const [editDrafts, setEditDrafts] = useState<Record<number, EditDraft | null>>({});

  // Per-space saving state
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());

  // Per-space member-add state: spaceId -> { userId, role }
  const [addState, setAddState] = useState<
    Record<number, { userId: string; role: "viewer" | "editor" }>
  >({});

  // Per-space dept-add state: spaceId -> { deptId, role }
  const [addDeptState, setAddDeptState] = useState<
    Record<number, { deptId: string; role: "viewer" | "editor" }>
  >({});

  // Confirm-delete state: `${spaceId}-${userId}`
  const [confirmDel, setConfirmDel] = useState<string | null>(null);

  // Confirm-revoke dept state: `${spaceId}-${deptId}`
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const toggleExpand = (id: number): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDeptExpand = (id: number): void => {
    setDeptExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startEdit = (id: number, name: string, color: string | null): void => {
    setEditDrafts((prev) => ({ ...prev, [id]: { name, color } }));
  };

  const cancelEdit = (id: number): void => {
    setEditDrafts((prev) => ({ ...prev, [id]: null }));
  };

  const saveEdit = async (id: number): Promise<void> => {
    const draft = editDrafts[id];
    if (!draft) return;
    const trimmed = draft.name.trim();
    if (!trimmed) return;
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      await renameAdminSpace(id, { name: trimmed, color: draft.color });
      setEditDrafts((prev) => ({ ...prev, [id]: null }));
      toastSuccess("Space aktualisiert.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const submitNew = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await createAdminSpace({ name });
      setNewName("");
      toastSuccess(`Space „${name}" angelegt.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (
    spaceId: number,
    userId: number,
    role: "viewer" | "editor"
  ): Promise<void> => {
    try {
      await assignAdminSpaceMember(spaceId, userId, role);
      toastSuccess("Rolle aktualisiert.");
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : "Rolle ändern fehlgeschlagen"
      );
    }
  };

  const handleRemove = async (
    spaceId: number,
    userId: number,
    memberName: string
  ): Promise<void> => {
    try {
      await removeAdminSpaceMember(spaceId, userId);
      setConfirmDel(null);
      toastSuccess(`${memberName} entfernt.`);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : "Entfernen fehlgeschlagen"
      );
    }
  };

  const handleAddMember = async (spaceId: number): Promise<void> => {
    const state = addState[spaceId];
    if (!state?.userId) return;
    try {
      await assignAdminSpaceMember(spaceId, Number(state.userId), state.role);
      setAddState((prev) => ({ ...prev, [spaceId]: { userId: "", role: "viewer" } }));
      toastSuccess("Mitglied hinzugefügt.");
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : "Hinzufügen fehlgeschlagen"
      );
    }
  };

  const handleGrantDept = async (spaceId: number): Promise<void> => {
    const state = addDeptState[spaceId];
    if (!state?.deptId) return;
    try {
      await grantAdminSpaceDepartment(spaceId, Number(state.deptId), state.role);
      setAddDeptState((prev) => ({ ...prev, [spaceId]: { deptId: "", role: "viewer" } }));
      toastSuccess("Abteilung zugewiesen.");
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : "Zuweisen fehlgeschlagen"
      );
    }
  };

  const handleRevokeDept = async (
    spaceId: number,
    deptId: number,
    deptName: string
  ): Promise<void> => {
    try {
      await revokeAdminSpaceDepartment(spaceId, deptId);
      setConfirmRevoke(null);
      toastSuccess(`Abteilung „${deptName}" entfernt.`);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : "Entfernen fehlgeschlagen"
      );
    }
  };

  return (
    <section class="space-y-4">
      {/* Create card */}
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4">
        <h3 class="text-sm font-medium text-[color:var(--color-on-surface)] mb-2">
          Neuer Space
        </h3>
        <div class="flex gap-2">
          <input
            type="text"
            value={newName}
            onInput={(e) => setNewName((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submitNew();
              }
            }}
            placeholder="z.B. Kundenportal"
            class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-label="Name des neuen Spaces"
          />
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={() => void submitNew()}
            disabled={!newName.trim() || creating}
          >
            <Plus size={14} aria-hidden="true" />
            Anlegen
          </Button>
        </div>
      </div>

      {/* List */}
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
        {spaces.loading ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)]">
            Lädt…
          </p>
        ) : spaces.error ? (
          <p class="p-4 text-sm text-[color:var(--color-error)]">
            {spaces.error}
          </p>
        ) : spaces.items.length === 0 ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)] italic">
            Noch keine Spaces.
          </p>
        ) : (
          <ul class="divide-y divide-[color:var(--color-outline-variant)]">
            {spaces.items.map((space) => {
              const expanded = expandedIds.has(space.id);
              const deptExpanded = deptExpandedIds.has(space.id);
              const add = addState[space.id] ?? { userId: "", role: "viewer" as const };
              const addDept = addDeptState[space.id] ?? { deptId: "", role: "viewer" as const };
              const draft = editDrafts[space.id] ?? null;
              const isSaving = savingIds.has(space.id);

              // Departments not yet granted to this space
              const availableDepts = depts.items.filter(
                (d) => !space.departments.some((sd) => sd.department_id === d.id)
              );

              return (
                <li key={space.id}>
                  {/* Row header */}
                  <div class="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <button
                      type="button"
                      onClick={() => toggleExpand(space.id)}
                      class="text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] transition-colors"
                      aria-expanded={expanded}
                      aria-label={expanded ? "Mitglieder zuklappen" : "Mitglieder aufklappen"}
                    >
                      {expanded ? (
                        <ChevronDown size={14} aria-hidden="true" />
                      ) : (
                        <ChevronRight size={14} aria-hidden="true" />
                      )}
                    </button>

                    {/* Color dot */}
                    <span
                      class="size-3 rounded-full shrink-0"
                      style={{
                        background: space.color ?? "var(--color-primary)",
                      }}
                      aria-hidden="true"
                    />

                    <span class="flex-1 min-w-0">
                      <span class="text-[color:var(--color-on-surface)] font-medium">
                        {space.name}
                      </span>
                      <span class="ml-2 text-[color:var(--color-on-surface-variant)] text-xs">
                        /{space.slug}
                      </span>
                    </span>

                    <span class="text-xs text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container)] rounded px-1.5 py-0.5">
                      {space.project_count} Projekte
                    </span>
                    <span class="inline-flex items-center gap-1 text-xs text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container)] rounded px-1.5 py-0.5">
                      <Users size={14} aria-hidden="true" />
                      {space.member_count}
                    </span>

                    {/* Departments toggle */}
                    <button
                      type="button"
                      onClick={() => toggleDeptExpand(space.id)}
                      class="inline-flex items-center gap-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container)] px-2 py-0.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high,var(--color-surface-container))] hover:text-[color:var(--color-on-surface)] transition-colors"
                      aria-expanded={deptExpanded}
                      aria-label={deptExpanded ? "Abteilungen zuklappen" : "Abteilungen aufklappen"}
                    >
                      <Building2 size={14} aria-hidden="true" />
                      Abteilungen ({space.departments.length})
                      {deptExpanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                    </button>

                    {/* Edit pencil */}
                    {draft === null ? (
                      <button
                        type="button"
                        onClick={() => startEdit(space.id, space.name, space.color)}
                        class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] transition-colors"
                        aria-label={`Space ${space.name} bearbeiten`}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => cancelEdit(space.id)}
                        class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] transition-colors"
                        aria-label="Bearbeitung abbrechen"
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>

                  {/* Edit panel: rename + color */}
                  {draft !== null && (
                    <div class="border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container)] px-4 py-3 space-y-2">
                      <p class="text-xs font-medium text-[color:var(--color-on-surface-variant)]">
                        Space bearbeiten
                      </p>
                      <input
                        type="text"
                        value={draft.name}
                        onInput={(e) =>
                          setEditDrafts((prev) => ({
                            ...prev,
                            [space.id]: { ...draft, name: (e.target as HTMLInputElement).value },
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void saveEdit(space.id);
                          }
                          if (e.key === "Escape") cancelEdit(space.id);
                        }}
                        class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                        aria-label="Name des Spaces"
                      />
                      {/* Color swatch picker */}
                      <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
                        {COLOR_PRESETS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() =>
                              setEditDrafts((prev) => ({
                                ...prev,
                                [space.id]: { ...draft, color: c },
                              }))
                            }
                            class={`size-5 rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                              draft.color === c
                                ? "border-[color:var(--color-on-surface)] scale-110"
                                : "border-transparent hover:scale-110"
                            }`}
                            style={{ background: c }}
                            aria-label={`Farbe ${c}`}
                            aria-pressed={draft.color === c}
                          />
                        ))}
                      </div>
                      <div class="flex items-center gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => cancelEdit(space.id)}
                          class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                        >
                          Abbrechen
                        </button>
                        <Button
                          variant="primary"
                          size="xs"
                          type="button"
                          onClick={() => void saveEdit(space.id)}
                          disabled={isSaving || !draft.name.trim()}
                        >
                          <Check size={14} aria-hidden="true" />
                          {isSaving ? "Speichere…" : "Speichern"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Departments panel */}
                  {deptExpanded && (
                    <div class="border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container)] px-4 py-3 space-y-2">
                      <p class="text-xs font-medium text-[color:var(--color-on-surface-variant)]">
                        Abteilungen
                      </p>

                      {/* Existing department chips */}
                      {space.departments.length === 0 ? (
                        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
                          Keine Abteilungen zugewiesen.
                        </p>
                      ) : (
                        <div class="flex flex-wrap gap-1.5">
                          {space.departments.map((sd) => {
                            const revokeKey = `${space.id}-${sd.department_id}`;
                            return (
                              <span
                                key={sd.department_id}
                                class="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-surface-container-low)] border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-xs text-[color:var(--color-on-surface)]"
                              >
                                <Building2 size={14} aria-hidden="true" class="text-[color:var(--color-on-surface-variant)]" />
                                {sd.name}
                                <span class="text-[color:var(--color-on-surface-variant)]">
                                  · {sd.role === "editor" ? "Bearbeiter" : "Betrachter"}
                                </span>
                                {confirmRevoke === revokeKey ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        void handleRevokeDept(space.id, sd.department_id, sd.name)
                                      }
                                      class="ml-1 rounded bg-[color:var(--color-error)] px-1.5 py-0.5 text-label text-[color:var(--color-on-error)] hover:opacity-90"
                                    >
                                      Entfernen
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setConfirmRevoke(null)}
                                      class="rounded border border-[color:var(--color-outline-variant)] px-1.5 py-0.5 text-label text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)]"
                                    >
                                      Abbrechen
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setConfirmRevoke(revokeKey)}
                                    class="ml-1 rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                                    aria-label={`Abteilung ${sd.name} entfernen`}
                                  >
                                    <X size={14} aria-hidden="true" />
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Add department */}
                      <div class="flex gap-2 pt-1 border-t border-[color:var(--color-outline-variant)]">
                        <select
                          value={addDept.deptId}
                          onChange={(e) =>
                            setAddDeptState((prev) => ({
                              ...prev,
                              [space.id]: {
                                ...addDept,
                                deptId: (e.target as HTMLSelectElement).value,
                              },
                            }))
                          }
                          class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label="Abteilung auswählen"
                        >
                          <option value="">Abteilung wählen…</option>
                          {availableDepts.map((d) => (
                            <option key={d.id} value={String(d.id)}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                        <select
                          value={addDept.role}
                          onChange={(e) =>
                            setAddDeptState((prev) => ({
                              ...prev,
                              [space.id]: {
                                ...addDept,
                                role: (e.target as HTMLSelectElement).value as "viewer" | "editor",
                              },
                            }))
                          }
                          class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label="Rolle für Abteilung"
                        >
                          <option value="viewer">Betrachter</option>
                          <option value="editor">Bearbeiter</option>
                        </select>
                        <Button
                          variant="primary"
                          size="md"
                          type="button"
                          onClick={() => void handleGrantDept(space.id)}
                          disabled={!addDept.deptId}
                        >
                          <Plus size={14} aria-hidden="true" />
                          Abteilung zuweisen
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Expanded member panel */}
                  {expanded && (
                    <div class="border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container)] px-4 py-3 space-y-2">
                      <p class="text-xs font-medium text-[color:var(--color-on-surface-variant)]">
                        Mitglieder
                      </p>
                      {/* Existing members */}
                      {space.members.length === 0 ? (
                        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
                          Keine Mitglieder zugewiesen.
                        </p>
                      ) : (
                        <ul class="space-y-1.5">
                          {space.members.map((m) => {
                            const delKey = `${space.id}-${m.user_id}`;
                            return (
                              <li
                                key={m.user_id}
                                class="flex items-center gap-2 text-sm"
                              >
                                <span class="flex-1 min-w-0">
                                  <span class="text-[color:var(--color-on-surface)]">
                                    {m.name}
                                  </span>
                                  <span class="ml-1 text-xs text-[color:var(--color-on-surface-variant)]">
                                    {m.email}
                                  </span>
                                </span>
                                <select
                                  value={m.role}
                                  onChange={(e) =>
                                    void handleRoleChange(
                                      space.id,
                                      m.user_id,
                                      (e.target as HTMLSelectElement)
                                        .value as "viewer" | "editor"
                                    )
                                  }
                                  class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                                >
                                  <option value="viewer">Betrachter</option>
                                  <option value="editor">Bearbeiter</option>
                                </select>
                                {confirmDel === delKey ? (
                                  <>
                                    <Button
                                      variant="destructive"
                                      size="xs"
                                      type="button"
                                      onClick={() =>
                                        void handleRemove(
                                          space.id,
                                          m.user_id,
                                          m.name
                                        )
                                      }
                                    >
                                      Wirklich entfernen
                                    </Button>
                                    <button
                                      type="button"
                                      onClick={() => setConfirmDel(null)}
                                      class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
                                    >
                                      Abbrechen
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDel(delKey)}
                                    class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                                    aria-label={`${m.name} entfernen`}
                                  >
                                    <Trash2 size={14} aria-hidden="true" />
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      {/* Add member */}
                      <div class="flex gap-2 pt-1 border-t border-[color:var(--color-outline-variant)]">
                        <select
                          value={add.userId}
                          onChange={(e) =>
                            setAddState((prev) => ({
                              ...prev,
                              [space.id]: {
                                ...add,
                                userId: (e.target as HTMLSelectElement).value,
                              },
                            }))
                          }
                          class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label="Benutzer auswählen"
                        >
                          <option value="">Benutzer wählen…</option>
                          {users
                            .filter(
                              (u) =>
                                !space.members.some(
                                  (m) => m.user_id === u.id
                                )
                            )
                            .map((u) => (
                              <option key={u.id} value={String(u.id)}>
                                {u.name} ({u.email})
                              </option>
                            ))}
                        </select>
                        <select
                          value={add.role}
                          onChange={(e) =>
                            setAddState((prev) => ({
                              ...prev,
                              [space.id]: {
                                ...add,
                                role: (e.target as HTMLSelectElement)
                                  .value as "viewer" | "editor",
                              },
                            }))
                          }
                          class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label="Rolle"
                        >
                          <option value="viewer">Betrachter</option>
                          <option value="editor">Bearbeiter</option>
                        </select>
                        <Button
                          variant="primary"
                          size="md"
                          type="button"
                          onClick={() => void handleAddMember(space.id)}
                          disabled={!add.userId}
                        >
                          <Plus size={14} aria-hidden="true" />
                          Hinzufügen
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
